// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Instance } from '../../api/client'
import { LinkButton } from '../../components/ui'

export function OnboardingSpotlight({ instance, onGoToAddons, onGoToBackups }: {
  instance: Instance
  onGoToAddons: () => void
  onGoToBackups: () => void
}) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`onboarding_dismissed_${instance.id}`) === '1' } catch { return false }
  })

  const dismiss = useCallback(() => {
    setDismissed(true)
    try { localStorage.setItem(`onboarding_dismissed_${instance.id}`, '1') } catch { /* noop */ }
  }, [instance.id])

  const isProvisioning = instance.status === 'provisioning' || instance.status === 'initializing'
  if (!isProvisioning || dismissed) return null

  return (
    <div className="mb-6 relative">
      <div className="absolute -inset-1 bg-gradient-to-r from-brand-400/20 via-purple-400/20 to-blue-400/20 rounded-2xl blur-sm" />
      <div className="relative bg-white border border-brand-200 rounded-xl p-6 shadow-sm">
        <LinkButton
          tone="neutral"
          onClick={dismiss}
          className="absolute top-3 right-3 text-lg leading-none"
          aria-label={t('detail.onboardingExtra.dismissAria')}
        >
          &times;
        </LinkButton>

        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
          <h3 className="font-semibold text-gray-900 text-sm">{t('detail.onboarding.title')}</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">{t('detail.onboarding.subtitle')}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Upload Addons card */}
          <button
            onClick={() => { onGoToAddons(); dismiss() }}
            className="group text-left border border-gray-200 hover:border-brand-300 rounded-xl p-4 transition-all hover:shadow-md hover:bg-brand-50/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 text-sm group-hover:text-brand-700">{t('detail.onboarding.addonsTitle')}</h4>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{t('detail.onboarding.addonsDesc')}</p>
            <span className="inline-block mt-2 text-xs font-medium text-brand-600 group-hover:text-brand-700">
              {t('detail.onboarding.addonsAction')} →
            </span>
          </button>

          {/* Upload Database Dump card */}
          <button
            onClick={() => { onGoToBackups(); dismiss() }}
            className="group text-left border border-gray-200 hover:border-purple-300 rounded-xl p-4 transition-all hover:shadow-md hover:bg-purple-50/50"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 text-sm group-hover:text-purple-700">{t('detail.onboarding.backupTitle')}</h4>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{t('detail.onboarding.backupDesc')}</p>
            <span className="inline-block mt-2 text-xs font-medium text-purple-600 group-hover:text-purple-700">
              {t('detail.onboarding.backupAction')} →
            </span>
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">{t('detail.onboarding.queueNote')}</p>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────
